import React, { useEffect, useMemo, useState } from 'react';
import { App, Button, Space, Spin } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, WarningFilled } from '@ant-design/icons';
import { useGetBatchPrintJobStatusQuery } from '../../services/cloudPrintJobsApi';
import { useHubEvent } from '../../hooks/useHubEvent';
import { PRINT_JOB_CHANGED_EVENT } from '../../services/realtimeHub';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { PrintBatch, printDismissed } from '../../store/slices/cloudPrintSlice';
import { useEnqueuePrintBatch } from '../../hooks/useCloudPrint';

type Phase = 'sending' | 'polling' | 'success' | 'error' | 'timeout';

const FAST_POLL_MS = 2000;
const SLOW_POLL_MS = 5000;
const FAST_POLL_DURATION_MS = 15000;
const TIMEOUT_MS = 60000;
/** Seconds the success toast lingers before closing itself. */
const SUCCESS_TOAST_SECONDS = 3;

const DONE_STATUSES = ['FAILED', 'CANCELLED', 'EXPIRED'];

/**
 * Watches ONE print batch and keeps a single toast (keyed by batch id) in sync with it.
 * Renders no DOM of its own — the toast lives in AntD's notification portal, so it survives
 * whatever modal or page the print was fired from being unmounted.
 */
const PrintBatchWatcher: React.FC<{ batch: PrintBatch }> = ({ batch }) => {
  const { notification } = App.useApp();
  const dispatch = useAppDispatch();
  const enqueueBatch = useEnqueuePrintBatch();

  const [phase, setPhase] = useState<Phase>('sending');
  const [elapsedMs, setElapsedMs] = useState(0);

  const { jobIds, startedAt } = batch;
  const isWatching = phase === 'sending' || phase === 'polling';
  const shouldPoll = isWatching && jobIds.length > 0;
  const pollingInterval = elapsedMs < FAST_POLL_DURATION_MS ? FAST_POLL_MS : SLOW_POLL_MS;

  // `currentData`, never `data`: RTK Query keeps `data` from the previous argument around, so on
  // a retry (which empties jobIds first) `data` would still hold the FAILED statuses of the last
  // attempt. The effect below would read them, flip straight back to 'error', and then never poll
  // the new job ids because shouldPoll is gated on the phase.
  const { currentData, refetch } = useGetBatchPrintJobStatusQuery(jobIds, {
    pollingInterval: shouldPoll ? pollingInterval : 0,
    skip: !shouldPoll,
  });

  // Belt and suspenders: only trust statuses that actually belong to the ids we're watching now.
  const jobStatuses = useMemo(() => {
    if (!currentData?.length) return undefined;
    const watching = new Set(jobIds.map(id => id.toLowerCase()));
    return currentData.every(j => watching.has(j.id.toLowerCase())) ? currentData : undefined;
  }, [currentData, jobIds]);

  // Realtime fast path: the agent reported COMPLETED/FAILED, refetch now instead of waiting for
  // the next poll. The polling above stays as the fallback.
  useHubEvent(
    PRINT_JOB_CHANGED_EVENT,
    (payload) => {
      const changedId = String(payload?.printJobId).toLowerCase();
      if (jobIds.some((id) => id.toLowerCase() === changedId)) refetch();
    },
    shouldPoll
  );

  // A retry resets startedAt, which restarts the clock and pulls the phase back to 'sending'.
  useEffect(() => {
    setPhase('sending');
    setElapsedMs(0);
  }, [startedAt]);

  useEffect(() => {
    if (phase === 'sending' && jobIds.length > 0) setPhase('polling');
  }, [jobIds.length, phase]);

  // Elapsed clock: switches the poll cadence and eventually gives up.
  useEffect(() => {
    if (!isWatching) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setElapsedMs(elapsed);
      if (elapsed >= TIMEOUT_MS) setPhase('timeout');
    }, 1000);
    return () => clearInterval(interval);
  }, [isWatching, startedAt]);

  useEffect(() => {
    if (!jobStatuses?.length || !isWatching) return;
    const completed = jobStatuses.filter(j => j.status === 'COMPLETED').length;
    const failed = jobStatuses.filter(j => DONE_STATUSES.includes(j.status)).length;
    if (completed === jobStatuses.length) setPhase('success');
    else if (failed > 0 && completed + failed === jobStatuses.length) setPhase('error');
  }, [jobStatuses, isWatching]);

  const close = () => dispatch(printDismissed(batch.id));

  const completedCount = jobStatuses?.filter(j => j.status === 'COMPLETED').length ?? 0;
  const totalCount = jobIds.length;
  const errorMessage = jobStatuses
    ?.filter(j => DONE_STATUSES.includes(j.status))
    .map(j => j.errorMessage || `Trabajo ${j.id.slice(0, 8)}… ${j.status}`)
    .join('; ');

  // Push/refresh the toast. notification.open with a stable `key` updates in place, so the same
  // toast walks through sending → imprimiendo → resultado without ever stacking.
  useEffect(() => {
    const common = {
      key: batch.id,
      placement: 'bottomRight' as const,
      onClose: close,
    };

    if (phase === 'sending' || phase === 'polling') {
      notification.open({
        ...common,
        icon: <Spin />,
        message: phase === 'sending' ? 'Enviando a imprimir…' : 'Imprimiendo…',
        description:
          totalCount > 1 ? `${batch.label} · ${completedCount}/${totalCount}` : batch.label,
        // Sticky while in flight, but closable: the user can dismiss it and keep working.
        duration: null,
      });
      return;
    }

    if (phase === 'success') {
      notification.open({
        ...common,
        icon: <CheckCircleFilled style={{ color: 'var(--kds-ok, #52c41a)' }} />,
        message: totalCount > 1 ? `${totalCount} tickets impresos` : 'Ticket impreso',
        description: batch.label,
        duration: SUCCESS_TOAST_SECONDS,
      });
      return;
    }

    const isTimeout = phase === 'timeout';
    notification.open({
      ...common,
      icon: isTimeout
        ? <WarningFilled style={{ color: '#faad14' }} />
        : <CloseCircleFilled style={{ color: '#ff4d4f' }} />,
      message: isTimeout ? 'La impresión no confirmó' : 'No se pudo imprimir',
      description: isTimeout
        ? `${batch.label} · sin respuesta en 60 s. Puede que sí haya salido.`
        : `${batch.label}${errorMessage ? ` · ${errorMessage}` : ''}`,
      duration: null,
      btn: (
        <Space>
          <Button size="small" onClick={close}>Cerrar</Button>
          <Button
            size="small"
            type="primary"
            onClick={() => enqueueBatch(batch.id, batch.label, batch.retry)}
          >
            Reintentar
          </Button>
        </Space>
      ),
    });
    // `close`/`enqueueBatch` are stable enough for this effect's purpose; re-running on every
    // identity change would rebuild the toast on unrelated renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, completedCount, totalCount, errorMessage, batch.id, batch.label]);

  // The batch left the store (dismissed, or the success toast expired) — take the toast with it.
  useEffect(() => () => notification.destroy(batch.id), [batch.id, notification]);

  return null;
};

/**
 * Mounted once, app-wide. Cloud print is fire-and-forget: useCloudPrint drops a batch in the
 * store and returns, and this is what tells the user how it went — without blocking the screen.
 */
const PrintStatusListener: React.FC = () => {
  const batches = useAppSelector((state) => state.cloudPrint.batches);
  return (
    <>
      {batches.map(batch => (
        <PrintBatchWatcher key={batch.id} batch={batch} />
      ))}
    </>
  );
};

export default PrintStatusListener;
